// Hero fills the viewport minus the fixed-height blocks that always sit above it
// (measured on https://w9.signnow.com/?hrld=1):
//   mobile  (<768px): sn-app-banner 56 + sn-navbar-type-2 74 + sn-breadcrumbs-type-2 69 (2 rows) = 199
//   desktop (>=768px): sn-navbar-type-2 74 + sn-breadcrumbs-type-2 45 (1 row)                    = 119
// Intentionally static — no JS measuring. Templates can override the custom
// property if a page stacks a different set of blocks above the hero.
$offset-mobile: 199px;
$offset-desktop: 119px;

.sn-hero-header-type-2 {
  --sn-hero-header-type-2-offset: #{$offset-mobile};

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100dvh - var(--sn-hero-header-type-2-offset));
  overflow: hidden;
  color: $color-neutral-white;
  text-align: center;
  margin-bottom: $spacing-3xl;

  @include desktop {
    --sn-hero-header-type-2-offset: #{$offset-desktop};
  }

  @include desktop-lg {
    margin-bottom: $spacing-6xl;
  }

  &::after {
    background-color: rgb(23 30 51 / 70%);
    inset: 0;
    content: "";
    display: block;
    height: 100%;
    position: absolute;
    width: 100%;
    z-index: 1;
  }

  &__background {
    left: calc(50% - 50vw);
    margin-top: 14px;
    max-height: 100vh;
    overflow: hidden;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100vw;
    z-index: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;

    @include desktop {
      left: calc(50% - 384.5px);
      margin-top: 0;
      max-height: none;
      overflow: visible;
      top: 0;
      transform: none;
      width: 769px;
    }
  }

  &__background-img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  &__content {
    position: relative;
    z-index: 2;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 720px;
    padding: 0 map-get($spacings, xl) map-get($spacings, 3xl);

    @include desktop {
      padding: 0 map-get($spacings, 2xl) map-get($spacings, 5xl);
    }
  }

  &__heading {
    font-size: map-get($font-sizes, 4xl);
    font-family: $sn-font-family-heading;
    font-weight: map-get($font-weights, bold);
    line-height: map-get($line-heights, 2xl);
    color: $color-neutral-white;
    margin: 0 0 map-get($spacings, lg);

    @include desktop {
      font-size: map-get($font-sizes, 6xl);
    }
  }

  &__heading-link {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover,
    &:focus-visible {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 2px solid $color-brand-primary-main;
      outline-offset: 4px;
      border-radius: map-get($border-radii, sm);
    }
  }

  &__description {
    font-size: map-get($font-sizes, xl);
    line-height: map-get($line-heights, lg);
    color: $color-neutral-neutral-lighter;
    margin: 0 0 map-get($spacings, 2xl);
    max-width: 600px;
  }

  &__cta {
    min-width: 214px;
  }

  // Intro button: 214×48. The height comes from the padding — 24px line-height + 2×2px border
  // + 2×10px padding. The theme sets `body.signnow [data-color-mode] .me-button--primary`
  // (specificity 0,3,1), which outranks the three classes below, so the `[data-color-mode]`
  // ancestor is matched too — that lifts this rule to (0,4,0) and the padding sticks.
  [data-color-mode] &__cta.me-button.me-button--primary {
    padding: 0.625rem 1.2rem;
  }

  &__details {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    padding: 0 map-get($spacings, xl) map-get($spacings, md);
    text-align: center;

    &--open {
      &::before {
        background-color: #373d4f;
        content: "";
        height: calc(100% + 16px);
        left: 50%;
        position: absolute;
        top: -16px;
        transform: translateX(-50%);
        width: 100vw;
        z-index: -1;
      }
    }
  }

  &__details-toggle {
    display: inline-flex;
    align-items: center;
    gap: map-get($spacings, xs);
    background: none;
    border: none;
    color: $color-neutral-white;
    font-size: map-get($font-sizes, sm);
    font-weight: map-get($font-weights, medium);
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: $color-brand-primary-light;
    }

    &:focus-visible {
      outline: 2px solid $color-brand-primary-main;
      outline-offset: 2px;
      border-radius: map-get($border-radii, sm);
    }
  }

  // The chevron SVG is fetched lazily, so the icon box is empty (0×0) until it
  // lands. Reserve its final footprint up front — otherwise the toggle grows by
  // 24px horizontally (plus the gap) and by 8px vertically the moment the SVG
  // arrives, and the centered label visibly jumps. `me-icon` mirrors its `class`
  // onto both the host and the inner `.me-icon` wrapper, so both get the box.
  &__details-icon {
    @include size(24px);

    display: inline-flex;
    flex-shrink: 0;
    transform: rotate(0deg);
    transition: transform 0.3s ease;
  }

  // `me-icon` mirrors its `class` onto BOTH its host element and its inner
  // `.me-icon` wrapper, so both nested nodes carry `&__details-icon--open`. A bare
  // `&__details-icon--open { transform: rotate(180deg) }` therefore rotates the two
  // nested elements and the turns compound to 360°. Scope the open rotation to the
  // host only (the me-icon is a direct child of the toggle; its inner wrapper is
  // not) so the chevron flips a single 180°.
  &__details-toggle > &__details-icon--open {
    transform: rotate(180deg);
  }

  &__details-panel {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.4s ease, opacity 0.3s ease;
    opacity: 0;

    &--open {
      max-height: 600px;
      opacity: 1;
    }
  }

  &__short-description {
    font-size: map-get($font-sizes, sm);
    line-height: map-get($line-heights, lg);
    color: $color-neutral-white;
    margin: map-get($spacings, md) auto 0;
    max-width: 780px;
    text-align: left;

    &--hidden {
      display: none;
    }
  }

  &__irs-description {
    font-size: map-get($font-sizes, xs);
    line-height: map-get($line-heights, md);
    color: $color-neutral-white;
    margin: map-get($spacings, sm) auto 0;

    &--hidden {
      display: none;
    }
  }
}
